import { useCallback, useContext, useMemo } from 'react';
import { useFormState } from 'react-final-form';
import { Listing } from 'models/Listing';
import { REQUIRED_VISIBLE_INFO } from 'data/global';
import { getIsDefaultRequired } from 'router/data/defaultRequired';
import { getArrayLength, getHasElementsInArray } from 'utils';
import ListingFieldsRequiredVisibleInfoContext from '../providers/ListingFieldsRequiredVisibleInfo/ListingFieldsRequiredVisibleInfo.context';
import {
  PUBLIC_LISTING_REQUIREMENTS,
  PublicListingRequirement,
  PublicListingRequirementKey,
} from '../data/publicListingRequirements';

export type PublicListingRequirementState = {
  key: PublicListingRequirementKey;
  fieldName?: string;
  /** The control inside that field to land on, where it opens as a panel. */
  focusFieldName?: string;
  isRequired: boolean;
  isSatisfied: boolean;
  /** Anything entered here, short of the rule being met. */
  hasInput: boolean;
  /** Came over with the deal rather than being entered on the page. */
  isPrefilledFromDeal: boolean;
};

/**
 * Resolves the shared rule set against the current form values.
 *
 * The financial entries defer to the per-listing `isRequired` override, so a
 * broker who marks revenue as not required is neither chased for it nor counted
 * against it. The override is read straight off the context rather than through
 * useListingFieldIsRequired, which calls useForm per field and resets field
 * state as a side effect.
 */
export default function usePublicListingRequirements(isUrgent: boolean) {
  const { values } = useFormState<Partial<Listing>>({
    subscription: { values: true },
  });

  const { fields } = useContext(ListingFieldsRequiredVisibleInfoContext);

  const getIsRequired = useCallback(
    (requirement: PublicListingRequirement) => {
      const { getRequiredInfoKeys } = requirement;

      if (!getRequiredInfoKeys) return true;

      const infoKeys = getRequiredInfoKeys(values);

      // No key to defer to - nothing here can be outstanding.
      if (!getHasElementsInArray(infoKeys)) return false;

      return infoKeys.some((infoKey) => {
        const override =
          fields?.[`${infoKey}${REQUIRED_VISIBLE_INFO}` as const]?.isRequired;

        return override ?? getIsDefaultRequired(infoKey, isUrgent);
      });
    },
    [fields, isUrgent, values],
  );

  const requirements = useMemo<PublicListingRequirementState[]>(
    () =>
      PUBLIC_LISTING_REQUIREMENTS.filter(
        ({ appliesTo }) => appliesTo?.(isUrgent) ?? true,
      ).map((requirement) => ({
        key: requirement.key,
        fieldName: requirement.getFieldName?.(isUrgent, values),
        focusFieldName: requirement.getFocusFieldName?.(values),
        isRequired: getIsRequired(requirement),
        isSatisfied: requirement.isSatisfied(values, isUrgent),
        hasInput: (requirement.hasInput ?? requirement.isSatisfied)(
          values,
          isUrgent,
        ),
        isPrefilledFromDeal: !!requirement.isPrefilledFromDeal?.(isUrgent),
      })),
    [getIsRequired, isUrgent, values],
  );

  const missingKeys = useMemo(
    () =>
      requirements
        .filter(({ isRequired, isSatisfied }) => isRequired && !isSatisfied)
        .map(({ key }) => key),
    [requirements],
  );

  /**
   * What the broker has entered on the page itself. Zero means nothing answered
   * yet, and an untouched draft is told what the page is for rather than counted
   * against - it is a starting point, not a shortfall.
   *
   * The deal room hands every draft a location and an industry, so a count of
   * everything satisfied would never read as untouched. Counted on input rather
   * than on the rule being met, so one highlight of the two the rule wants is
   * already a draft being worked on.
   */
  const enteredCount = useMemo(
    () =>
      getArrayLength(
        requirements.filter(
          ({ hasInput, isPrefilledFromDeal }) =>
            hasInput && !isPrefilledFromDeal,
        ),
      ),
    [requirements],
  );

  const isRequired = useCallback(
    (key: PublicListingRequirementKey) =>
      !!requirements.find((requirement) => requirement.key === key)?.isRequired,
    [requirements],
  );

  const isSatisfied = useCallback(
    (key: PublicListingRequirementKey) =>
      !!requirements.find((requirement) => requirement.key === key)
        ?.isSatisfied,
    [requirements],
  );

  return {
    requirements,
    missingKeys,
    missingCount: getArrayLength(missingKeys),
    enteredCount,
    isRequired,
    isSatisfied,
  };
}
